import { useCallback, useEffect, useMemo, useRef, useState } from "react"
import { Trans, useLingui } from "@lingui/react/macro"
import { getCsrfToken } from "@/lib/csrf"
import { camelizeKeys } from "@/lib/utils"
import { toast } from "@/components/ui/sonner"
import {
  SparkleIcon,
  ChatBubbleIcon,
  SearchIcon,
  ChevronRightIcon,
  CheckIcon,
} from "./icons"
import type {
  CallFollowUpResponse,
  CallFollowUpTemplate,
  CallFollowUpTemplateOption,
  ReplyWatchResponse,
  VoiceCallRecord,
} from "@/types/v2/voice"

// WhatsApp follow-up sub-view, reached from the post-call
// card's no-answer "Message on WhatsApp" pivot (CON-158/CON-160).
// Fetches the follow-up context and renders one of three dark
// surfaces matching the Paper "approve-the-draft" frames:
//
//   17 · in 24h window      → freeform composer (green send)
//   18 · template           → AI-chosen approved template, with
//                             per-variable inputs (green send)
//   20 · outside · blocked  → no approved template exists, so
//                             we pivot (schedule a call-back)
//                             instead of dead-ending
//
// Both sends route through the dedicated, window-re-checking
// `/follow_up` endpoint so a stale client can't push a freeform
// send after the window has closed.
//
// `previewData` is dev-only: when supplied (by the voice-card
// harness) the live fetch is skipped so each branch can be
// browser-tested without a billed call or seeded window state.
export function WhatsAppFollowUp({
  voiceCall,
  contactName,
  onBack,
  onSent,
  previewData = null,
}: {
  voiceCall: VoiceCallRecord
  contactName: string
  onBack: () => void
  onSent: () => void
  previewData?: CallFollowUpResponse | null
}) {
  const { t } = useLingui()
  const [loading, setLoading] = useState(!previewData)
  const [followUp, setFollowUp] = useState<CallFollowUpResponse | null>(
    previewData,
  )
  const [freeform, setFreeform] = useState(
    previewData?.freeformSuggestion ?? "",
  )
  // Per-variable input state: current values plus which tokens
  // the AI filled (drives the "— AI filled" labels). Seeded
  // explicitly — never via a blanket template effect — so a
  // value the rep types while a picked template's AI refill is
  // in flight survives the refill landing (CON-202).
  const [variables, setVariables] = useState<TemplateVariableState>(() =>
    seedVariables(previewData?.template ?? null),
  )
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)
  // The in-widget template picker (frame 18b), reached from the
  // template composer's "Change". Back closes it — never the
  // whole follow-up. (CON-202)
  const [pickerOpen, setPickerOpen] = useState(false)
  // The AI refill of a freshly-picked template (frame 18c):
  // "filling" while the ?whatsapp_template_id= fetch is in
  // flight, "failed" when it errored (the rep fills manually).
  const [refillState, setRefillState] = useState<
    "idle" | "filling" | "failed"
  >("idle")
  // Monotonic pick counter so a stale refill response (the rep
  // re-picked mid-flight) is dropped instead of applied.
  const refillSeqRef = useRef(0)
  // The armed "notify me when they reply" watch (frame 22).
  // Non-null once the rep arms it from the blocked edge.
  const [watch, setWatch] = useState<ReplyWatchResponse | null>(null)
  const [arming, setArming] = useState(false)

  // First name only, matching the design header ("WhatsApp · John").
  const firstName = useMemo(
    () => contactName.trim().split(/\s+/)[0] || contactName,
    [contactName],
  )

  useEffect(() => {
    if (previewData) return
    let cancelled = false
    void (async () => {
      try {
        const response = await fetch(
          `/v2/channels/voice/calls/${voiceCall.id}/follow_up`,
          {
            headers: { Accept: "application/json" },
            credentials: "same-origin",
          },
        )
        if (!response.ok) throw new Error("follow_up")
        // This is a raw `render json:` endpoint — Inertia's
        // prop_transformer does NOT camelize fetch responses,
        // so the body arrives snake_case. Camelize it the same
        // way `useChannel` does before consuming it (keys are
        // camelized, string values left intact — so the
        // template tokens/suggestions survive).
        const data = camelizeKeys(await response.json()) as CallFollowUpResponse
        if (cancelled) return
        setFollowUp(data)
        setFreeform(data.freeformSuggestion ?? "")
        setVariables(seedVariables(data.template ?? null))
      } catch {
        if (!cancelled) {
          setError(t`We couldn't load the follow-up. Please try again.`)
        }
      } finally {
        if (!cancelled) setLoading(false)
      }
    })()
    return () => {
      cancelled = true
    }
  }, [voiceCall.id, previewData])

  const conversationId = followUp?.conversationId ?? null
  const template = followUp?.template ?? null

  // The picker's option list, with the currently-staged
  // template guaranteed present (prepended if the approved
  // list somehow doesn't carry it) so "Selected now" always
  // has a row to flag.
  const templateOptions = useMemo(() => {
    const options = followUp?.templates ?? []
    if (!template) return options
    if (options.some((option) => option.id === template.id)) return options
    return [
      {
        id: template.id,
        name: template.name,
        body: template.body,
        variables: template.variables.map((v) => v.token),
      },
      ...options,
    ]
  }, [followUp?.templates, template])

  // Frame 18c — the rep picked a template from the picker.
  // The current one is a no-op; any other is staged
  // immediately (name + body, empty variables — typeable right
  // away) while the AI refill fetch runs. In the dev harness
  // the pick is visual only: staged, never fetched.
  const pickTemplate = useCallback(
    (option: CallFollowUpTemplateOption) => {
      setPickerOpen(false)
      if (template && option.id === template.id) return
      const staged: CallFollowUpTemplate = {
        id: option.id,
        name: option.name,
        body: option.body,
        variables: option.variables.map((token) => ({
          token,
          suggestion: "",
        })),
      }
      setFollowUp((prev) => (prev ? { ...prev, template: staged } : prev))
      const values: Record<number, string> = {}
      for (const token of option.variables) values[token] = ""
      setVariables({ values, aiTokens: [] })
      if (previewData) {
        setRefillState("idle")
        return
      }
      setRefillState("filling")
      const seq = ++refillSeqRef.current
      void (async () => {
        try {
          const response = await fetch(
            `/v2/channels/voice/calls/${voiceCall.id}/follow_up` +
              `?whatsapp_template_id=${option.id}`,
            {
              headers: { Accept: "application/json" },
              credentials: "same-origin",
            },
          )
          if (!response.ok) throw new Error("refill")
          const data = camelizeKeys(
            await response.json(),
          ) as CallFollowUpResponse
          if (seq !== refillSeqRef.current) return
          const refilled = data.template
          if (!refilled || refilled.id !== option.id) throw new Error("refill")
          // Fill ONLY the blanks — a value the rep typed while
          // the fetch was in flight is never clobbered.
          setVariables((prev) => {
            const values = { ...prev.values }
            const aiTokens = new Set(prev.aiTokens)
            for (const v of refilled.variables) {
              if ((values[v.token] ?? "").trim().length > 0) continue
              const suggestion = v.suggestion ?? ""
              values[v.token] = suggestion
              if (suggestion.trim().length > 0) aiTokens.add(v.token)
            }
            return { values, aiTokens: [...aiTokens] }
          })
          setRefillState("idle")
        } catch {
          if (seq !== refillSeqRef.current) return
          setRefillState("failed")
        }
      })()
    },
    [template, previewData, voiceCall.id],
  )

  const postFollowUp = useCallback(
    async (payload: Record<string, unknown>) => {
      // In the dev harness the buttons are visual only — never
      // fire a real send against the seeded preview call.
      if (previewData) {
        toast.success(t`Preview — not actually sent`)
        return
      }
      setSubmitting(true)
      setError(null)
      try {
        const response = await fetch(
          `/v2/channels/voice/calls/${voiceCall.id}/follow_up`,
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              Accept: "application/json",
              "X-CSRF-Token": getCsrfToken(),
            },
            body: JSON.stringify(payload),
            credentials: "same-origin",
          },
        )
        if (!response.ok) throw new Error("send")
        toast.success(t`Follow-up sent`)
        onSent()
      } catch {
        setError(t`We couldn't send that. Please try again.`)
      } finally {
        setSubmitting(false)
      }
    },
    [voiceCall.id, onSent, previewData],
  )

  const sendFreeform = useCallback(() => {
    if (!conversationId || submitting || freeform.trim().length === 0) return
    void postFollowUp({ message: freeform.trim() })
  }, [conversationId, submitting, freeform, postFollowUp])

  const sendTemplate = useCallback(() => {
    if (!conversationId || !template || submitting) return
    const allFilled = template.variables.every(
      (v) => (variables.values[v.token] ?? "").trim().length > 0,
    )
    if (!allFilled) return
    void postFollowUp({
      whatsapp_template_id: template.id,
      template_variables: variables.values,
    })
  }, [conversationId, template, submitting, variables.values, postFollowUp])

  // Arm a "notify me when they reply" watch (frame 20 →
  // frame 22). Outside the window with no template, this saves
  // the AI draft and pings the rep the moment the contact's
  // reply reopens the window. In the dev harness the button is
  // visual only.
  const armWatch = useCallback(() => {
    if (!conversationId || arming) return
    if (previewData) {
      setWatch({
        id: 0,
        status: "pending",
        followUpDraft: previewData.freeformSuggestion ?? null,
        conversationId,
        contactName: contactName,
      })
      return
    }
    setArming(true)
    setError(null)
    void (async () => {
      try {
        const response = await fetch(
          `/v2/inbox/conversations/${conversationId}/reply_watch`,
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              Accept: "application/json",
              "X-CSRF-Token": getCsrfToken(),
            },
            body: JSON.stringify({ voice_call_id: voiceCall.id }),
            credentials: "same-origin",
          },
        )
        if (!response.ok) throw new Error("arm")
        const data = camelizeKeys(
          await response.json(),
        ) as ReplyWatchResponse
        setWatch(data)
      } catch {
        setError(t`We couldn't set that up. Please try again.`)
      } finally {
        setArming(false)
      }
    })()
  }, [conversationId, arming, previewData, voiceCall.id, contactName])

  const cancelWatch = useCallback(() => {
    if (!conversationId) return
    setWatch(null)
    if (previewData) return
    void fetch(`/v2/inbox/conversations/${conversationId}/reply_watch`, {
      method: "DELETE",
      headers: {
        "X-CSRF-Token": getCsrfToken(),
        Accept: "application/json",
      },
      credentials: "same-origin",
    })
  }, [conversationId, previewData])

  // Which of the three surfaces to render.
  const view = watch
    ? "armed"
    : loading
    ? "loading"
    : error && !followUp
      ? "error"
      : conversationId == null
        ? "noConversation"
        : followUp?.withinWindow
          ? "inWindow"
          : template
            ? "template"
            : followUp && !followUp.hasApprovedTemplates
              ? "blocked"
              : "noDraft"

  return (
    <div
      data-testid="after-call-follow-up"
      className="flex flex-col gap-[13px] px-[18px] pb-4 pt-[18px] text-[#FBF7F0]"
    >
      {view === "armed" ? null : (
        <Header
          firstName={firstName}
          showWhatsAppIcon={view === "inWindow"}
          // From the picker, Back returns to the template
          // composer — never all the way out to the card.
          onBack={
            pickerOpen && view === "template"
              ? () => setPickerOpen(false)
              : onBack
          }
        />
      )}

      {view === "loading" ? (
        <p className="text-sm text-[#9A938A]"><Trans>Loading the follow-up…</Trans></p>
      ) : view === "error" ? (
        <p role="alert" className="text-sm text-[#9A938A]">
          {error}
        </p>
      ) : view === "noConversation" ? (
        <p
          data-testid="after-call-follow-up-no-conversation"
          className="text-sm text-[#9A938A]"
        >
          <Trans>
            There's no WhatsApp conversation with {contactName} yet — start one
            from the inbox to send a follow-up.
          </Trans>
        </p>
      ) : view === "inWindow" ? (
        <InWindowComposer
          value={freeform}
          onChange={setFreeform}
          onSend={sendFreeform}
          submitting={submitting}
          error={error}
        />
      ) : view === "template" && template ? (
        pickerOpen ? (
          <TemplatePicker
            templates={templateOptions}
            currentTemplateId={template.id}
            onPick={pickTemplate}
          />
        ) : (
          <TemplateComposer
            firstName={firstName}
            template={template}
            variableValues={variables.values}
            aiTokens={variables.aiTokens}
            refillState={refillState}
            onChangeVariable={(token, value) =>
              setVariables((prev) => ({
                ...prev,
                values: { ...prev.values, [token]: value },
              }))
            }
            onChangeTemplate={() => setPickerOpen(true)}
            onSend={sendTemplate}
            submitting={submitting}
            error={error}
          />
        )
      ) : view === "armed" && watch ? (
        <WatchArmed
          firstName={firstName}
          draft={watch.followUpDraft}
          onDone={onSent}
          onCancel={cancelWatch}
        />
      ) : view === "blocked" ? (
        <BlockedEdge
          firstName={firstName}
          onScheduleCallBack={onBack}
          onNotify={armWatch}
          arming={arming}
          error={error}
        />
      ) : (
        <p
          data-testid="after-call-follow-up-no-draft"
          className="text-sm text-[#9A938A]"
        >
          <Trans>
            We couldn't draft a follow-up just now. Please try again in a moment.
          </Trans>
        </p>
      )}
    </div>
  )
}

// Shared sub-view header (frames 17/18/20): a brand-amber "Back"
// link on the left, the channel + contact on the right. The
// in-window surface shows a green WhatsApp glyph.
function Header({
  firstName,
  showWhatsAppIcon,
  onBack,
}: {
  firstName: string
  showWhatsAppIcon: boolean
  onBack: () => void
}) {
  const { t } = useLingui()
  return (
    <div className="flex items-center gap-2">
      <button
        type="button"
        onClick={onBack}
        className="text-[12px] font-medium text-[#E0A56A] hover:underline"
      >
        <Trans>‹ Back</Trans>
      </button>
      <div className="grow" />
      {showWhatsAppIcon ? (
        <ChatBubbleIcon width={15} height={15} className="text-[#7FD79A]" />
      ) : null}
      <span className="text-[12px] font-semibold text-[#FBF7F0]">
        {t`WhatsApp · ${firstName}`}
      </span>
    </div>
  )
}

// Frame 17 — inside the 24h window: a freeform composer seeded
// with the AI draft and a green Send button.
function InWindowComposer({
  value,
  onChange,
  onSend,
  submitting,
  error,
}: {
  value: string
  onChange: (value: string) => void
  onSend: () => void
  submitting: boolean
  error: string | null
}) {
  const { t } = useLingui()
  return (
    <>
      <div className="flex items-center gap-1.5 self-start rounded-lg bg-[#22361F] px-2.5 py-1.5">
        <span
          aria-hidden="true"
          className="size-1.5 shrink-0 rounded-full bg-[#7FD79A]"
        />
        <span className="text-[11px] text-[#A7E8C0]">
          <Trans>Within the 24h window · free text</Trans>
        </span>
      </div>

      <div className="flex items-center gap-1.5">
        <SparkleIcon width={12} height={12} className="text-[#E0A56A]" />
        <span className="text-[11px] text-[#E0A56A]">
          <Trans>AI suggested</Trans>
        </span>
      </div>

      <textarea
        value={value}
        onChange={(e) => onChange(e.target.value)}
        aria-label={t`WhatsApp follow-up message`}
        rows={3}
        className="min-h-[84px] w-full resize-none rounded-[10px] border border-[#3A352E] bg-transparent px-[11px] py-[11px] text-[13px] leading-[19px] text-[#FBF7F0] placeholder:text-[#6E685F] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#E0A56A]/40"
        placeholder={t`Write a quick follow-up…`}
      />

      {error ? (
        <p role="alert" className="text-xs text-[#9A938A]">
          {error}
        </p>
      ) : null}

      <button
        type="button"
        onClick={onSend}
        disabled={submitting || value.trim().length === 0}
        data-testid="after-call-follow-up-send"
        className="flex items-center justify-center rounded-[11px] bg-[#1FAA53] px-4 py-[11px] text-[13px] font-semibold text-white hover:bg-[#1B9A4B] disabled:opacity-50"
      >
        {submitting ? <Trans>Sending…</Trans> : <Trans>Send WhatsApp</Trans>}
      </button>
    </>
  )
}

// Per-variable input state for the template composer: the
// editable values plus which tokens the AI filled (drives the
// "— AI filled" labels). Kept as one state so the refill merge
// updates both atomically. (CON-202)
interface TemplateVariableState {
  values: Record<number, string>
  aiTokens: number[]
}

// Seed the variable state from a drafted template's per-token
// AI suggestions (the initial fetch / preview payload).
function seedVariables(
  template: CallFollowUpTemplate | null,
): TemplateVariableState {
  const values: Record<number, string> = {}
  const aiTokens: number[] = []
  if (template) {
    for (const v of template.variables) {
      values[v.token] = v.suggestion ?? ""
      if ((v.suggestion ?? "").trim().length > 0) aiTokens.push(v.token)
    }
  }
  return { values, aiTokens }
}

// Frame 18 — outside the window, an approved template exists:
// the AI picked + filled it. The rep can correct each variable
// before the green Send. The body is read-only (the template is
// approved; only the variables are editable).
function TemplateComposer({
  firstName,
  template,
  variableValues,
  aiTokens,
  refillState,
  onChangeVariable,
  onChangeTemplate,
  onSend,
  submitting,
  error,
}: {
  firstName: string
  template: NonNullable<CallFollowUpResponse["template"]>
  variableValues: Record<number, string>
  aiTokens: number[]
  refillState: "idle" | "filling" | "failed"
  onChangeVariable: (token: number, value: string) => void
  onChangeTemplate: () => void
  onSend: () => void
  submitting: boolean
  error: string | null
}) {
  const { t } = useLingui()
  const allFilled = template.variables.every(
    (v) => (variableValues[v.token] ?? "").trim().length > 0,
  )
  return (
    <>
      <div className="flex items-start gap-[7px] rounded-lg bg-[#33301F] px-2.5 py-2">
        <WarningTriangleIcon
          width={13}
          height={13}
          className="mt-px shrink-0 text-[#E0C68A]"
        />
        <span className="text-[11px] leading-[15px] text-[#E0C68A]">
          <Trans>
            It's been over 24h since {firstName} last messaged. Only an approved
            template can send.
          </Trans>
        </span>
      </div>

      <div className="flex flex-col gap-1.5">
        <div className="flex items-center gap-1.5">
          <span className="grow text-[11px] font-semibold text-[#9A938A]">
            {t`TEMPLATE · ${template.name}`}
          </span>
          <button
            type="button"
            onClick={onChangeTemplate}
            data-testid="after-call-follow-up-change-template"
            className="text-[11px] text-[#E0A56A] hover:underline"
          >
            <Trans>Change</Trans>
          </button>
        </div>
        <div className="rounded-[10px] border border-[#3A352E] px-[11px] py-[11px]">
          <span className="text-[13px] leading-[19px] text-[#D9D3C8]">
            {template.body}
          </span>
        </div>
      </div>

      {template.variables.map((v) => {
        const value = variableValues[v.token] ?? ""
        // While the picked template's AI refill is in flight
        // the blanks pulse and the label says so; after a
        // failure the labels go plain (the rep fills manually).
        const label =
          refillState === "filling"
            ? t`{{${v.token}}} — AI filling from the call…`
            : aiTokens.includes(v.token)
              ? t`{{${v.token}}} — AI filled`
              : `{{${v.token}}}`
        const pulsing =
          refillState === "filling" && value.trim().length === 0
        return (
          <div key={v.token} className="flex flex-col gap-1.5">
            <span className="text-[11px] text-[#9A938A]">{label}</span>
            <input
              type="text"
              value={value}
              onChange={(e) => onChangeVariable(v.token, e.target.value)}
              aria-label={t`Template variable ${v.token}`}
              className={`rounded-lg border border-[#3A352E] bg-transparent px-2.5 py-2 text-[13px] text-[#FBF7F0] placeholder:text-[#6E685F] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#E0A56A]/40${
                pulsing ? " animate-pulse" : ""
              }`}
            />
          </div>
        )
      })}

      {refillState === "failed" ? (
        <p role="alert" className="text-xs text-[#9A938A]">
          <Trans>We couldn't fill this template — add the details and send.</Trans>
        </p>
      ) : null}

      {error ? (
        <p role="alert" className="text-xs text-[#9A938A]">
          {error}
        </p>
      ) : null}

      <button
        type="button"
        onClick={onSend}
        disabled={submitting || !allFilled}
        data-testid="after-call-follow-up-send-template"
        className="flex items-center justify-center rounded-[11px] bg-[#1FAA53] px-4 py-[11px] text-[13px] font-semibold text-white hover:bg-[#1B9A4B] disabled:opacity-50"
      >
        {submitting ? <Trans>Sending…</Trans> : <Trans>Send template</Trans>}
      </button>
    </>
  )
}

// Frame 18b — the in-widget template picker, reached from the
// template composer's "Change". Search + one row per approved
// template, the currently-staged one flagged "Selected now".
// Styled after the Link-a-deal PickStep (same dark rows).
function TemplatePicker({
  templates,
  currentTemplateId,
  onPick,
}: {
  templates: CallFollowUpTemplateOption[]
  currentTemplateId: number
  onPick: (option: CallFollowUpTemplateOption) => void
}) {
  const { t } = useLingui()
  const [search, setSearch] = useState("")
  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase()
    if (!q) return templates
    return templates.filter(
      (tmpl) =>
        tmpl.name.toLowerCase().includes(q) ||
        (tmpl.body ?? "").toLowerCase().includes(q),
    )
  }, [templates, search])

  return (
    <div
      data-testid="follow-up-template-picker"
      className="flex flex-col gap-[13px]"
    >
      <div className="flex flex-col gap-1">
        <span className="text-[15px] font-semibold text-[#FBF7F0]">
          <Trans>Choose a template</Trans>
        </span>
        <span className="text-[12px] leading-[17px] text-[#9A938A]">
          <Trans>
            Only an approved template can send outside the 24h window. AI
            refills the blanks for whichever you pick.
          </Trans>
        </span>
      </div>

      <div className="flex items-center gap-2 rounded-[10px] border border-[#3A352E] bg-[#252019] px-[11px] py-[9px]">
        <SearchIcon width={14} height={14} className="shrink-0 text-[#6E685F]" />
        <input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          aria-label={t`Search templates`}
          placeholder={t`Search templates…`}
          className="grow bg-transparent text-[13px] text-[#FBF7F0] placeholder:text-[#6E685F] focus:outline-none"
        />
      </div>

      <div className="flex flex-col gap-[7px]">
        <span className="text-[11px] font-semibold uppercase tracking-[0.04em] text-[#9A938A]">
          <Trans>Approved templates</Trans>
        </span>
        {filtered.length === 0 ? (
          <p className="px-1 py-2 text-[13px] text-[#9A938A]">
            <Trans>No templates match "{search}".</Trans>
          </p>
        ) : (
          filtered.map((option) => {
            const isCurrent = option.id === currentTemplateId
            return (
              <button
                key={option.id}
                type="button"
                onClick={() => onPick(option)}
                aria-current={isCurrent}
                data-testid="follow-up-template-option"
                className={`flex items-center gap-2.5 rounded-[11px] border px-3 py-2.5 text-left hover:bg-[#2A2620] ${
                  isCurrent
                    ? "border-[#E0A56A]/50 bg-[#2A2620]"
                    : "border-[#3A352E] bg-[#252019]"
                }`}
              >
                <span className="flex min-w-0 grow flex-col gap-px">
                  <span className="truncate text-[13px] font-medium text-[#FBF7F0]">
                    {option.name}
                  </span>
                  <span className="line-clamp-2 text-[12px] leading-[17px] text-[#9A938A]">
                    {option.body}
                  </span>
                  {isCurrent ? (
                    <span className="text-[11px] text-[#7FD79A]">
                      <Trans>Selected now</Trans>
                    </span>
                  ) : null}
                </span>
                {isCurrent ? (
                  <CheckIcon
                    width={13}
                    height={13}
                    className="shrink-0 text-[#7FD79A]"
                  />
                ) : (
                  <ChevronRightIcon
                    width={7}
                    height={12}
                    className="shrink-0 text-[#6E685F]"
                  />
                )}
              </button>
            )
          })
        )}
      </div>

      <p className="text-[11px] text-[#6E685F]">
        <Trans>Templates are managed in Settings → WhatsApp.</Trans>
      </p>
    </div>
  )
}

// Frame 20 — outside the window with NO approved template. Never
// a dead-end: explain why WhatsApp is blocked, then pivot to the
// call-back the rep already has queued AND offer to ping them the
// moment the contact replies (which reopens the window → free
// text). (The follow-up-template setting link arrives with
// CON-161.)
function BlockedEdge({
  firstName,
  onScheduleCallBack,
  onNotify,
  arming,
  error,
}: {
  firstName: string
  onScheduleCallBack: () => void
  onNotify: () => void
  arming: boolean
  error: string | null
}) {
  const { t } = useLingui()
  return (
    <>
      <div className="flex flex-col gap-1.5 rounded-[12px] bg-[#2A2620] px-3.5 py-[13px]">
        <span className="text-[13px] font-semibold text-[#FBF7F0]">
          {t`Can't message ${firstName} on WhatsApp yet`}
        </span>
        <span className="text-[12px] leading-[18px] text-[#9A938A]">
          <Trans>
            It's been over 24h, so WhatsApp only allows an approved template — and
            you don't have one set up. Here's what works instead:
          </Trans>
        </span>
      </div>

      <button
        type="button"
        onClick={onScheduleCallBack}
        data-testid="after-call-follow-up-schedule-call-back"
        className="flex items-center gap-[11px] rounded-[12px] border border-[#3A352E] bg-[#252019] px-[13px] py-3 text-left hover:bg-[#2A2620]"
      >
        <span className="flex size-[30px] shrink-0 items-center justify-center rounded-lg bg-[#2F2A24]">
          <ClockIcon width={15} height={15} className="text-[#E7E2D9]" />
        </span>
        <span className="flex min-w-0 grow flex-col gap-px">
          <span className="text-[13px] font-medium text-[#FBF7F0]">
            <Trans>Schedule a call-back</Trans>
          </span>
          <span className="truncate text-[12px] text-[#9A938A]">
            <Trans>Already suggested on this call</Trans>
          </span>
        </span>
      </button>

      <button
        type="button"
        onClick={onNotify}
        disabled={arming}
        data-testid="after-call-follow-up-notify"
        className="flex items-center gap-[11px] rounded-[12px] border border-[#3A352E] bg-[#252019] px-[13px] py-3 text-left hover:bg-[#2A2620] disabled:opacity-50"
      >
        <span className="flex size-[30px] shrink-0 items-center justify-center rounded-lg bg-[#2F2A24]">
          <ChatBubbleIcon width={15} height={15} className="text-[#7FD79A]" />
        </span>
        <span className="flex min-w-0 grow flex-col gap-px">
          <span className="text-[13px] font-medium text-[#FBF7F0]">
            {arming
              ? t`Setting up…`
              : t`Notify me when ${firstName} replies`}
          </span>
          <span className="truncate text-[12px] text-[#9A938A]">
            <Trans>Reopens the 24h window to free-text</Trans>
          </span>
        </span>
      </button>

      {error ? (
        <p role="alert" className="text-xs text-[#9A938A]">
          {error}
        </p>
      ) : null}
    </>
  )
}

// Frame 22 — the armed watch. Confirms we'll ping the rep when
// the contact replies, shows the saved AI draft, and offers
// Done (dismiss) / Cancel watch.
function WatchArmed({
  firstName,
  draft,
  onDone,
  onCancel,
}: {
  firstName: string
  draft: string | null
  onDone: () => void
  onCancel: () => void
}) {
  const { t } = useLingui()
  return (
    <div
      data-testid="after-call-follow-up-watch-armed"
      className="flex flex-col gap-3.5"
    >
      <div className="flex items-center gap-3">
        <span className="flex size-10 shrink-0 items-center justify-center rounded-full bg-[#22361F]">
          <ChatCheckIcon width={20} height={20} className="text-[#7FD79A]" />
        </span>
        <div className="flex flex-col gap-0.5">
          <span className="text-[15px] font-semibold leading-[18px] text-[#FBF7F0]">
            {t`We'll ping you when ${firstName} replies`}
          </span>
          <span className="text-[12px] leading-4 text-[#9A938A]">
            <Trans>
              Their reply reopens the 24h window — you'll be able to free-text
              then.
            </Trans>
          </span>
        </div>
      </div>

      {draft ? (
        <div className="flex items-start gap-2.5 rounded-[11px] border border-[#3A352E] bg-[#252019] px-3 py-[11px]">
          <SparkleIcon
            width={13}
            height={13}
            className="mt-px shrink-0 text-[#E0A56A]"
          />
          <div className="flex flex-col gap-0.5">
            <span className="text-[12px] font-medium text-[#D9D3C8]">
              <Trans>Your draft is saved</Trans>
            </span>
            <span className="text-[12px] leading-[17px] text-[#9A938A]">
              <Trans>"{draft}" — ready to send the moment they reply.</Trans>
            </span>
          </div>
        </div>
      ) : null}

      <div className="flex items-center gap-2.5">
        <button
          type="button"
          onClick={onDone}
          data-testid="after-call-follow-up-watch-done"
          className="grow rounded-[11px] bg-[#F4EFE6] px-4 py-2.5 text-[13px] font-semibold text-[#1E1B17] hover:bg-[#EBE4D6]"
        >
          <Trans>Done</Trans>
        </button>
        <button
          type="button"
          onClick={onCancel}
          data-testid="after-call-follow-up-watch-cancel"
          className="rounded-[11px] border border-[#3A352E] px-3.5 py-2.5 text-[13px] font-medium text-[#9A938A] hover:bg-[#2A2620]"
        >
          <Trans>Cancel watch</Trans>
        </button>
      </div>
    </div>
  )
}

function ChatCheckIcon(props: React.SVGProps<SVGSVGElement>) {
  return (
    <svg
      width={16}
      height={16}
      viewBox="0 0 16 16"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.3}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      {...props}
    >
      <path d="M8 1.5C4.4 1.5 1.5 4.4 1.5 8c0 1.2.3 2.3.9 3.3L1.5 14.5l3.3-.9c1 .5 2 .8 3.2.8 3.6 0 6.5-2.9 6.5-6.5S11.6 1.5 8 1.5Z" />
      <path d="M5.5 8l1.6 1.6L10.5 6.2" />
    </svg>
  )
}

function WarningTriangleIcon(props: React.SVGProps<SVGSVGElement>) {
  return (
    <svg
      width={16}
      height={16}
      viewBox="0 0 16 16"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.2}
      strokeLinejoin="round"
      aria-hidden="true"
      {...props}
    >
      <path d="M8 1.5L15 13.5H1L8 1.5Z" />
      <path d="M8 6.5V9.5" strokeLinecap="round" />
    </svg>
  )
}

function ClockIcon(props: React.SVGProps<SVGSVGElement>) {
  return (
    <svg
      width={16}
      height={16}
      viewBox="0 0 16 16"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.3}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      {...props}
    >
      <circle cx={8} cy={8} r={6} />
      <path d="M8 5v3l2 1.5" />
    </svg>
  )
}
